import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Map center constant (Connecticut area)
const DEFAULT_MAP_CENTER = [41.6032, -73.0877];

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 24px;
      height: 24px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 2px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });
};

// Simple hash function to generate consistent offset from claim_id
const hashCode = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash;
};

// Generate consistent offset based on claim_id
const getSeededOffset = (claimId, scale = 0.02) => {
  const hash = hashCode(claimId || 'default');
  const normalized = ((hash % 1000) / 1000) - 0.5; // -0.5 to 0.5
  return normalized * scale;
};

// US State coordinates (approximate centers)
const STATE_COORDS = {
  'AL': [32.806671, -86.791130], 'AK': [61.370716, -152.404419], 'AZ': [33.729759, -111.431221],
  'AR': [34.969704, -92.373123], 'CA': [36.116203, -119.681564], 'CO': [39.059811, -105.311104],
  'CT': [41.597782, -72.755371], 'DE': [39.318523, -75.507141], 'FL': [27.766279, -81.686783],
  'GA': [33.040619, -83.643074], 'HI': [21.094318, -157.498337], 'ID': [44.240459, -114.478828],
  'IL': [40.349457, -88.986137], 'IN': [39.849426, -86.258278], 'IA': [42.011539, -93.210526],
  'KS': [38.526600, -96.726486], 'KY': [37.668140, -84.670067], 'LA': [31.169546, -91.867805],
  'ME': [44.693947, -69.381927], 'MD': [39.063946, -76.802101], 'MA': [42.230171, -71.530106],
  'MI': [43.326618, -84.536095], 'MN': [45.694454, -93.900192], 'MS': [32.741646, -89.678696],
  'MO': [38.456085, -92.288368], 'MT': [46.921925, -110.454353], 'NE': [41.125370, -98.268082],
  'NV': [38.313515, -117.055374], 'NH': [43.452492, -71.563896], 'NJ': [40.298904, -74.521011],
  'NM': [34.840515, -106.248482], 'NY': [42.165726, -74.948051], 'NC': [35.630066, -79.806419],
  'ND': [47.528912, -99.784012], 'OH': [40.388783, -82.764915], 'OK': [35.565342, -96.928917],
  'OR': [44.572021, -122.070938], 'PA': [40.590752, -77.209755], 'RI': [41.680893, -71.511780],
  'SC': [33.856892, -80.945007], 'SD': [44.299782, -99.438828], 'TN': [35.747845, -86.692345],
  'TX': [31.054487, -97.563461], 'UT': [40.150032, -111.862434], 'VT': [44.045876, -72.710686],
  'VA': [37.769337, -78.169968], 'WA': [47.400902, -121.490494], 'WV': [38.491226, -80.954453],
  'WI': [44.268543, -89.616508], 'WY': [42.755966, -107.302490], 'DC': [38.897438, -77.026817],
  'Connecticut': [41.597782, -72.755371], 'Massachusetts': [42.230171, -71.530106],
  'New York': [42.165726, -74.948051], 'New Jersey': [40.298904, -74.521011],
  'Pennsylvania': [40.590752, -77.209755], 'Rhode Island': [41.680893, -71.511780],
};

// Common CT towns with approximate coords
const CT_TOWNS = {
  'hartford': [41.7658, -72.6734], 'new haven': [41.3083, -72.9279], 'stamford': [41.0534, -73.5387],
  'bridgeport': [41.1865, -73.1952], 'waterbury': [41.5582, -73.0515], 'norwalk': [41.1176, -73.4079],
  'danbury': [41.3948, -73.4540], 'new britain': [41.6612, -72.7795], 'bristol': [41.6718, -72.9493],
  'meriden': [41.5382, -72.8070], 'milford': [41.2223, -73.0565], 'west haven': [41.2707, -72.9470],
  'middletown': [41.5623, -72.6506], 'shelton': [41.3165, -73.0932], 'torrington': [41.8007, -73.1212],
  'windsor': [41.8526, -72.6437], 'tolland': [41.8715, -72.3687], 'terryville': [41.6779, -73.0115],
  'glastonbury': [41.7123, -72.6081], 'manchester': [41.7759, -72.5215], 'enfield': [41.9762, -72.5918],
};

// Get coordinates for a claim based on town/state (deterministic based on claim_id)
const getClaimCoords = (claim) => {
  const town = (claim.town || '').toLowerCase().trim();
  const state = (claim.state || '').trim();
  const claimId = claim.claim_id || '';
  
  // Use claim_id to generate consistent offsets
  const latOffset = getSeededOffset(claimId + '_lat', 0.02);
  const lngOffset = getSeededOffset(claimId + '_lng', 0.02);
  
  // Check CT towns first
  if (state === 'CT' || state === 'Connecticut') {
    if (CT_TOWNS[town]) {
      return [CT_TOWNS[town][0] + latOffset, CT_TOWNS[town][1] + lngOffset];
    }
  }
  
  // Fall back to state center with larger offset
  if (STATE_COORDS[state]) {
    const stateLatOffset = getSeededOffset(claimId + '_lat', 0.3);
    const stateLngOffset = getSeededOffset(claimId + '_lng', 0.3);
    return [STATE_COORDS[state][0] + stateLatOffset, STATE_COORDS[state][1] + stateLngOffset];
  }
  
  // Default to Connecticut if no match
  return [41.6032 + latOffset, -73.0877 + lngOffset];
};

// Component to fit map bounds to markers
const FitBounds = ({ claimsWithCoords }) => {
  const map = useMap();
  
  useEffect(() => {
    if (claimsWithCoords.length > 0) {
      const bounds = claimsWithCoords.map(c => c.coords);
      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
      }
    }
  }, [claimsWithCoords, map]);
  
  return null;
};

// Status badge classes
const STATUS_BADGE_CLASSES = {
  'Open': 'bg-blue-100 text-blue-700',
  'Pending Docs': 'bg-yellow-100 text-yellow-700',
  'Negotiation': 'bg-purple-100 text-purple-700',
  'Settled': 'bg-green-100 text-green-700',
  'Paid in Full': 'bg-emerald-100 text-emerald-700',
  'Closed': 'bg-gray-100 text-gray-700',
};

// Status colors
const STATUS_COLORS = {
  'Open': '#3B82F6',
  'Pending Docs': '#F59E0B',
  'Negotiation': '#8B5CF6',
  'Settled': '#10B981',
  'Paid in Full': '#059669',
  'Closed': '#6B7280',
};

export const ClaimsMap = ({ claims, expanded, onToggleExpand }) => {
  const [hoveredClaim, setHoveredClaim] = useState(null);
  
  // Memoize claims with coordinates so they don't change on re-render
  const claimsWithCoords = useMemo(() => {
    return claims.map(claim => ({
      ...claim,
      coords: getClaimCoords(claim),
    }));
  }, [claims]);

  // Memoize event handlers to prevent re-renders
  const createEventHandlers = useCallback((claim) => ({
    mouseover: () => setHoveredClaim(claim),
    mouseout: () => setHoveredClaim(null),
  }), []);

  const mapHeight = expanded ? '500px' : '250px';
  
  return (
    <div className="relative" data-testid="claims-map">
      {/* Map Container */}
      <div style={{ height: mapHeight, transition: 'height 0.3s ease' }} className="rounded-lg overflow-hidden">
        <MapContainer
          center={DEFAULT_MAP_CENTER}
          zoom={8}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={expanded}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds claimsWithCoords={claimsWithCoords} />
          
          {claimsWithCoords.map((claim) => (
            <Marker
              key={claim.claim_id}
              position={claim.coords}
              icon={createCustomIcon(STATUS_COLORS[claim.status] || '#6B7280')}
              eventHandlers={createEventHandlers(claim)}
            >
              <Popup>
                <div className="min-w-[200px]">
                  <p className="font-bold text-sm mb-1">{claim.claim_id}</p>
                  <p className="text-sm text-gray-700">{claim.customer_name}</p>
                  <p className="text-xs text-gray-500 mt-1">{claim.town}, {claim.state}</p>
                  <div className="flex justify-between items-center mt-2 pt-2 border-t">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE_CLASSES[claim.status] || 'bg-gray-100 text-gray-700'}`}>
                      {claim.status}
                    </span>
                    <span className="text-xs font-mono font-semibold">
                      ${(claim.expected_loss_settlement || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      
      {/* Hover Info Card */}
      {hoveredClaim && !expanded && (
        <div className="absolute top-2 right-2 bg-white rounded-lg shadow-lg p-3 z-[1000] max-w-[220px]">
          <p className="font-bold text-xs text-gray-800">{hoveredClaim.claim_id}</p>
          <p className="text-sm text-gray-700 truncate">{hoveredClaim.customer_name}</p>
          <p className="text-xs text-gray-500">{hoveredClaim.town}, {hoveredClaim.state}</p>
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-gray-600">{hoveredClaim.status}</span>
            <span className="text-xs font-mono font-semibold text-green-600">
              ${(hoveredClaim.expected_loss_settlement || 0).toLocaleString()}
            </span>
          </div>
        </div>
      )}
      
      {/* Expand/Collapse Button */}
      <button
        onClick={onToggleExpand}
        className="absolute bottom-2 right-2 z-[1000] bg-white hover:bg-gray-100 text-gray-700 px-3 py-1.5 rounded-md shadow-md text-sm font-medium transition-colors"
        data-testid="map-expand-toggle"
      >
        {expanded ? 'Collapse' : 'Expand Map'}
      </button>
      
      {/* Legend */}
      <div className="absolute bottom-2 left-2 z-[1000] bg-white/90 rounded-md shadow-sm p-2 text-xs">
        <div className="flex flex-wrap gap-2">
          {Object.entries(STATUS_COLORS).slice(0, 4).map(([status, color]) => (
            <div key={status} className="flex items-center gap-1">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: color }}
              />
              <span className="text-gray-600">{status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
